import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { useRef } from "react";
import { authentication, persistenceReady } from "@/firebase";
import EmailLogin from "@/components/EmailLogin";
import ButtonComponent from "@/components/ButtonComponent";
import GoogleImage from "@/assets/images/googleLogo.png";
import MicrosoftImage from "@/assets/images/microLogo.png";
import CleverImage from "@/assets/images/cleverLogo.png";
import CarouselPage from "@/components/CarouselPage";
import { logAuthEvent } from "../../utils/logAuthEvent";
import { getCleverRedirectUri } from "@/utils/auth/clever";
import { useAuth } from "@/features/auth/useAuth";
import "./SigninScreen.css";
import "../../assets/css/authpages.css";
import "../../components/components.css";

const cleverClientID = "81c607e331c7da3c5547";
const redirect_uri = getCleverRedirectUri();

const AuthMethods = ({ setLoginFailure }) => {
  const { firebaseLogin } = useAuth();
  const popupInFlightRef = useRef(false);

  async function GoogleAuth() {
    if (popupInFlightRef.current) return;
    popupInFlightRef.current = true;
    try {
      await persistenceReady;
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(authentication, provider);
      const idToken = await result.user.getIdToken();
      await firebaseLogin(idToken);
      logAuthEvent({
        message: "Google login successful",
        capture: true,
        level: "info",
      });
    } catch (error) {
      logAuthEvent({
        message: "Google login failed",
        error,
        level: "error",
        capture: true,
      });
    } finally {
      popupInFlightRef.current = false;
    }
  }

  async function MicrosoftAuth() {
    if (popupInFlightRef.current) return;
    popupInFlightRef.current = true;
    window.__loginStart = performance.now();
    window.__loginTimingTracked = false;
    try {
      await persistenceReady;
      const microProvider = new OAuthProvider("microsoft.com");
      microProvider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(authentication, microProvider);
      const idToken = await result.user.getIdToken();
      await firebaseLogin(idToken);
      logAuthEvent({
        message: "Microsoft login successful",
        capture: true,
        level: "info",
      });
    } catch (error) {
      if (error.code === "auth/account-exists-with-different-credential") {
        setLoginFailure?.(true);
      }
      logAuthEvent({
        message: "Microsoft login failed",
        error,
        level: "error",
        capture: true,
      });
    } finally {
      popupInFlightRef.current = false;
    }
  }

  function CleverAuth() {
    try {
      window.__loginStart = performance.now();
      window.__loginTimingTracked = false;
      if (!redirect_uri || !cleverClientID) {
        throw new Error("Missing Clever redirect_uri or client_id");
      }
      window.location.href = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${encodeURIComponent(redirect_uri)}&client_id=${cleverClientID}`;
      logAuthEvent({
        method: "Clever",
        success: true,
        message: "Redirected to Clever login",
      });
    } catch (error) {
      logAuthEvent({
        message: "Clever auth redirect failed",
        error,
        level: "error",
        capture: true,
      });
    }
  }

  return (
    <div>
      <button
        className="button-parent"
        onClick={GoogleAuth}
      >
        <ButtonComponent image={GoogleImage} txt="Sign In With Google" />
      </button>
      <button
        className="button-parent"
        onClick={MicrosoftAuth}
      >
        <ButtonComponent image={MicrosoftImage} txt="Sign In Microsoft" />
      </button>
      <button className="button-parent" onClick={CleverAuth}>
        <ButtonComponent image={CleverImage} txt="Sign In With Clever" />
      </button>
    </div>
  );
};

export const Footer = () => {
  return (
    <>
      <div className="bottom-text">
        <p>© GrowthWell LLC {new Date().getFullYear()}</p>

        <p>U.S. Patent No. 12,053,300</p>
        <p className="footer-links">
          <a
            href="https://www.123wellness.org/privacypolicy"
            className="bottom-text"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          <img
            id="separator_line"
            className="line"
            src="../images/line_9.png"
            alt="separator line"
          />

          <a
            href="https://www.123wellness.org/data-governance"
            className="bottom-text "
            target="_blank"
            rel="noreferrer"
          >
            Data Governance
          </a>

          <img
            id="separator_line"
            className="line"
            src="../images/line_9.png"
            alt="separator_line"
          />
          <a
            href="https://www.123wellness.org/terms-of-service"
            className="bottom-text "
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>
        </p>
      </div>
    </>
  );
};

const SigninScreen = () => {
  return (
    <div className="auth-container">
      <div className="row">
        <div className="container1">
          <div className="content">
            <div>
              <img
                className="logo-image-top"
                alt="logo"
                src="../images/123WellnessLogo green-02.png"
              />
            </div>
            <div className="top-text">
              <p className="text-wrapper">
                Welcome to the 1-2-3 Wellness™ app!
              </p>
            </div>

            <AuthMethods />
            <div className="line">
              <p className="text-center login_input_field" id="line_with_or">
                OR
              </p>
            </div>

            <EmailLogin />
          </div>
          <div className="bottom-text">
            <Footer />
          </div>
        </div>
        <div className="container2">
          <CarouselPage />
        </div>
      </div>
    </div>
  );
};

export default SigninScreen;
